import {
  WORK_ORDER_CATEGORY_LABELS,
  WORK_ORDER_SEVERITIES,
  WORK_ORDER_SEVERITY_LABELS,
  WORK_ORDER_TRANSITIONS,
  WORK_ORDER_STATUS_LABELS,
  type WorkOrderSeverity,
  type WorkOrderStatus,
} from '@sitewise/shared';
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  ErrorState,
  Input,
  Label,
  LoadingState,
  PageHeader,
  SeverityBadge,
  cn,
  toast,
} from '@sitewise/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import imageCompression from 'browser-image-compression';
import {
  ArrowLeft,
  Camera,
  Check,
  CheckCircle2,
  ChevronDown,
  Image as ImageIcon,
  MessageSquare,
  Trash2,
  X,
} from 'lucide-react';
import {
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type FormEvent,
} from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';

import { fetchMe } from '../../../api/auth.js';
import { listTeams } from '../../../api/teams.js';
import { fetchVendorCompanies } from '../../../api/vendor-companies.js';
import {
  acknowledgeVendorSignoff,
  assignWorkOrder,
  commentOnWorkOrder,
  confirmWorkOrderAttachment,
  deleteWorkOrder,
  deleteWorkOrderAttachment,
  fetchWorkOrder,
  presignWorkOrderAttachment,
  transitionWorkOrder,
  updateWorkOrder,
  type WorkOrderAttachmentRow,
  type WorkOrderEventRow,
} from '../../../api/work-orders.js';
import { AiSuggestionChip } from '../../../components/ai-suggestion-chip.js';
import { ApiError } from '../../../lib/api-client.js';

import { formatDateTime, isOverdue, relativeTime, StatusPill } from './lib.js';

export function WorkOrderDetailPage() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const meQuery = useQuery({ queryKey: ['me'], queryFn: fetchMe });
  const isTabletMode = meQuery.data?.user.tabletMode === true;
  const canDelete =
    meQuery.data?.user.role === 'admin' || meQuery.data?.user.isSuperAdmin === true;
  const detailQuery = useQuery({
    queryKey: ['work-order', id],
    queryFn: () => fetchWorkOrder(id!),
    enabled: Boolean(id),
  });

  const deleteMutation = useMutation({
    mutationFn: () => deleteWorkOrder(id!),
    onSuccess: () => {
      toast.success('Work order deleted');
      void queryClient.invalidateQueries({ queryKey: ['work-orders'] });
      void queryClient.invalidateQueries({ queryKey: ['work-order-stats'] });
      navigate('/work-orders');
    },
    onError: (err) => {
      toast.error(err instanceof ApiError ? err.message : 'Could not delete work order');
    },
  });

  const transitionMutation = useMutation({
    mutationFn: (toStatus: WorkOrderStatus) =>
      transitionWorkOrder(id!, { toStatus, notes: null, cancellationReason: null, completionNotes: null }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['work-order', id] });
      void queryClient.invalidateQueries({ queryKey: ['work-orders'] });
      void queryClient.invalidateQueries({ queryKey: ['work-order-stats'] });
    },
    onError: (err) => {
      toast.error(err instanceof ApiError ? err.message : 'Transition failed');
    },
  });

  const severityMutation = useMutation({
    mutationFn: (severity: WorkOrderSeverity) => updateWorkOrder(id!, { severity }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['work-order', id] });
      void queryClient.invalidateQueries({ queryKey: ['work-orders'] });
    },
    onError: (err) => {
      toast.error(err instanceof ApiError ? err.message : 'Severity change failed');
    },
  });

  const dueMutation = useMutation({
    mutationFn: (input: {
      dueAt?: string | null;
      remindBeforeDays?: 1 | 2 | 3 | null;
    }) => updateWorkOrder(id!, input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['work-order', id] });
      void queryClient.invalidateQueries({ queryKey: ['reminders'] });
    },
    onError: (err) => {
      toast.error(err instanceof ApiError ? err.message : 'Could not update due date');
    },
  });

  const isLoading = detailQuery.isLoading;
  if (isLoading) {
    return (
      <>
        <PageHeader title="Loading…" />
        <LoadingState rows={6} />
      </>
    );
  }
  if (detailQuery.isError) {
    return (
      <ErrorState
        description={
          detailQuery.error instanceof ApiError
            ? detailQuery.error.message
            : 'We could not load this work order.'
        }
      />
    );
  }
  if (!detailQuery.data) return null;

  const {
    workOrder: wo,
    building,
    unit,
    events,
    attachments,
    users,
    vendor,
    pendingVendorSignoffs,
  } = detailQuery.data;

  const allowedNext = (WORK_ORDER_TRANSITIONS[wo.status] ?? []).filter((s) => {
    if (!isTabletMode) return true;
    return s !== 'completed' && s !== 'cancelled';
  });

  return (
    <>
      <Link to="/work-orders" className="inline-flex items-center gap-1 text-sm text-fg-secondary hover:text-fg">
        <ArrowLeft className="h-3.5 w-3.5" />
        All work orders
      </Link>
      {isTabletMode ? (
        <div className="mb-3 rounded-md border border-border bg-surface-muted px-3 py-2 text-sm text-fg-secondary">
          Tablet mode — view only for closing. Complete or cancel this work order from a phone login.
        </div>
      ) : null}
      <PageHeader
        title={
          <div className="flex flex-col gap-1">
            <span className="text-xs font-mono text-fg-secondary">{wo.displayNumber}</span>
            <span>{wo.title}</span>
          </div>
        }
        description={
          building ? (
            <Link to={`/buildings/${building.id}`} className="text-fg-secondary underline-offset-2 hover:underline">
              {building.name} · {building.addressLine1}
              {unit ? ` · Unit ${unit.unitNumber}` : ''}
            </Link>
          ) : null
        }
        actions={
          <div className="flex items-center gap-2">
            <StatusPill status={wo.status} />
            <SeverityBadge level={wo.severity} subtle />
            {canDelete && !isTabletMode ? (
              <Button
                variant="destructive"
                size="sm"
                disabled={deleteMutation.isPending}
                onClick={() => {
                  if (
                    window.confirm(
                      `Delete ${wo.displayNumber}? This soft-deletes the work order.`,
                    )
                  ) {
                    deleteMutation.mutate();
                  }
                }}
              >
                <Trash2 className="h-3.5 w-3.5" />
                Delete
              </Button>
            ) : null}
          </div>
        }
      />

      <AiSuggestionChip
        workOrderId={wo.id}
        currentCategory={wo.category}
        currentSeverity={wo.severity}
        suggestion={{
          category: wo.aiSuggestedCategory ?? null,
          severity: wo.aiSuggestedSeverity ?? null,
          confidence: wo.aiSuggestionConfidence ?? null,
          rationale: wo.aiSuggestionRationale ?? null,
          suggestedAt: wo.aiSuggestedAt ?? null,
        }}
      />

      {pendingVendorSignoffs.length > 0 ? (
        <div className="mb-4 flex items-start gap-3 rounded-lg border border-warning/60 bg-warning-subtle px-4 py-3 text-sm">
          <CheckCircle2 className="mt-0.5 h-4 w-4 text-warning" />
          <div className="flex-1">
            <p className="font-medium text-fg">Vendor signed off — pending review</p>
            <p className="text-fg-secondary">
              {pendingVendorSignoffs.length === 1 ? '1 sign-off' : `${pendingVendorSignoffs.length} sign-offs`} waiting for org confirmation.
              Mark the work order completed when you have verified the work.
            </p>
          </div>
          <VendorAckButton
            workOrderId={wo.id}
            signoffId={pendingVendorSignoffs[0]!.id}
            onAcknowledged={() => queryClient.invalidateQueries({ queryKey: ['work-order', wo.id] })}
          />
        </div>
      ) : null}

      <div className="grid gap-4 lg:grid-cols-[1fr_320px]">
        <div className="flex flex-col gap-4">
          {/* Description */}
          {wo.description ? (
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Description</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="whitespace-pre-wrap text-sm text-fg-secondary">{wo.description}</p>
              </CardContent>
            </Card>
          ) : null}

          {/* Photos strip */}
          <PhotosStrip
            workOrderId={wo.id}
            attachments={attachments}
            onChange={() => queryClient.invalidateQueries({ queryKey: ['work-order', wo.id] })}
          />

          {/* Voice memos */}
          <VoiceMemosStrip attachments={attachments} />

          {/* Action bar — transitions */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Move to</CardTitle>
            </CardHeader>
            <CardContent className="flex flex-wrap gap-2">
              {allowedNext.length === 0 ? (
                <span className="text-sm text-fg-muted">No further transitions.</span>
              ) : (
                allowedNext.map((s) => (
                  <Button
                    key={s}
                    variant={s === 'completed' ? 'default' : 'secondary'}
                    size="sm"
                    onClick={() => transitionMutation.mutate(s)}
                    disabled={transitionMutation.isPending}
                  >
                    {s === 'completed' ? <CheckCircle2 className="h-3.5 w-3.5" /> : null}
                    {WORK_ORDER_STATUS_LABELS[s]}
                  </Button>
                ))
              )}
            </CardContent>
          </Card>

          {/* Comment composer */}
          <CommentComposer workOrderId={wo.id} />

          <EmailUpdatesCard events={events} />

          {/* Timeline */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Activity</CardTitle>
            </CardHeader>
            <CardContent>
              <Timeline events={events} users={users} attachments={attachments} />
            </CardContent>
          </Card>
        </div>

        <aside className="flex flex-col gap-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Details</CardTitle>
            </CardHeader>
            <CardContent className="text-sm">
              <DL>
                <DT>Severity</DT>
                <DD>
                  <select
                    className="h-8 rounded border border-border bg-surface px-2 text-sm"
                    value={wo.severity}
                    onChange={(e) => severityMutation.mutate(e.target.value as WorkOrderSeverity)}
                    disabled={severityMutation.isPending}
                  >
                    {WORK_ORDER_SEVERITIES.map((s) => (
                      <option key={s} value={s}>
                        {WORK_ORDER_SEVERITY_LABELS[s]}
                      </option>
                    ))}
                  </select>
                </DD>

                <DT>Status</DT>
                <DD>
                  <StatusPill status={wo.status} />
                </DD>

                <DT>Category</DT>
                <DD>{WORK_ORDER_CATEGORY_LABELS[wo.category as keyof typeof WORK_ORDER_CATEGORY_LABELS] ?? wo.category}</DD>

                <DT>Due</DT>
                <DD>
                  <DueDateInput
                    value={wo.dueAt}
                    overdue={isOverdue(wo.dueAt, wo.status)}
                    onChange={(iso) => dueMutation.mutate({ dueAt: iso })}
                    disabled={dueMutation.isPending}
                  />
                </DD>

                <DT>Remind</DT>
                <DD>
                  <select
                    className="h-8 w-full rounded border border-border bg-surface px-2 text-sm"
                    value={
                      typeof wo.metadata?.dueRemindBeforeDays === 'number'
                        ? String(wo.metadata.dueRemindBeforeDays)
                        : ''
                    }
                    disabled={!wo.dueAt || dueMutation.isPending}
                    onChange={(e) => {
                      const v = e.target.value;
                      const remindBeforeDays =
                        v === '1' || v === '2' || v === '3'
                          ? (Number(v) as 1 | 2 | 3)
                          : null;
                      dueMutation.mutate({ remindBeforeDays });
                    }}
                    aria-label="Remind before due date"
                  >
                    <option value="">No reminder</option>
                    <option value="1">1 day before</option>
                    <option value="2">2 days before</option>
                    <option value="3">3 days before</option>
                  </select>
                </DD>

                <DT>Submitted</DT>
                <DD>{formatDateTime(wo.createdAt)}</DD>

                <DT>Submitted by</DT>
                <DD>{wo.submittedBy ? users[wo.submittedBy]?.name ?? 'Unknown' : '—'}</DD>

                <DT>Submitted via</DT>
                <DD className="capitalize">{wo.submittedVia.replace(/_/g, ' ')}</DD>

                {wo.completedAt ? (
                  <>
                    <DT>Completed</DT>
                    <DD>{formatDateTime(wo.completedAt)}</DD>
                    <DT>Completed by</DT>
                    <DD>{wo.completedBy ? users[wo.completedBy]?.name ?? 'Unknown' : '—'}</DD>
                  </>
                ) : null}

                {wo.cancelledAt ? (
                  <>
                    <DT>Cancelled</DT>
                    <DD>{formatDateTime(wo.cancelledAt)}</DD>
                    <DT>Reason</DT>
                    <DD>{wo.cancellationReason ?? '—'}</DD>
                  </>
                ) : null}
              </DL>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-base">Team</CardTitle>
            </CardHeader>
            <CardContent>
              <TeamPicker
                workOrderId={wo.id}
                teamId={wo.assignedTeamId}
                onChanged={() => queryClient.invalidateQueries({ queryKey: ['work-order', wo.id] })}
              />
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-base">Vendor</CardTitle>
            </CardHeader>
            <CardContent>
              <VendorPicker
                workOrderId={wo.id}
                vendor={vendor}
                onChanged={() => queryClient.invalidateQueries({ queryKey: ['work-order', wo.id] })}
              />
            </CardContent>
          </Card>
        </aside>
      </div>
    </>
  );
}

function TeamPicker({
  workOrderId,
  teamId,
  onChanged,
}: {
  workOrderId: string;
  teamId: string | null;
  onChanged: () => void;
}) {
  const teamsQuery = useQuery({
    queryKey: ['teams'],
    queryFn: listTeams,
  });
  const assignMutation = useMutation({
    mutationFn: (nextTeamId: string | null) =>
      assignWorkOrder(workOrderId, {
        teamId: nextTeamId,
        userId: undefined,
        vendorId: undefined,
        notes: null,
      }),
    onSuccess: () => {
      toast.success('Team assignment updated');
      onChanged();
    },
    onError: (err) => toast.error(err instanceof ApiError ? err.message : 'Could not change team'),
  });

  return (
    <select
      className="h-9 w-full rounded-md border border-border bg-surface px-2 text-sm"
      value={teamId ?? ''}
      onChange={(e) => assignMutation.mutate(e.target.value || null)}
      disabled={assignMutation.isPending || teamsQuery.isLoading}
      aria-label="Assign team"
    >
      <option value="">Unassigned</option>
      {(teamsQuery.data?.teams ?? []).map((t) => (
        <option key={t.id} value={t.id}>
          {t.name}
        </option>
      ))}
    </select>
  );
}

function VendorPicker({
  workOrderId,
  vendor,
  onChanged,
}: {
  workOrderId: string;
  vendor: { id: string; name: string } | null;
  onChanged: () => void;
}) {
  const companiesQuery = useQuery({
    queryKey: ['vendor-companies'],
    queryFn: fetchVendorCompanies,
  });
  const assignMutation = useMutation({
    mutationFn: (vendorId: string | null) =>
      assignWorkOrder(workOrderId, {
        teamId: undefined,
        userId: undefined,
        vendorId,
        notes: null,
      }),
    onSuccess: (_data, nextVendorId) => {
      toast.success(
        nextVendorId
          ? 'Vendor assigned — technicians will be emailed'
          : 'Vendor unassigned',
      );
      onChanged();
    },
    onError: (err) => toast.error(err instanceof ApiError ? err.message : 'Could not change vendor'),
  });

  return (
    <div className="space-y-2">
      <select
        className="h-9 w-full rounded-md border border-border bg-surface px-2 text-sm"
        value={vendor?.id ?? ''}
        onChange={(e) => assignMutation.mutate(e.target.value || null)}
        disabled={assignMutation.isPending || companiesQuery.isLoading}
        aria-label="Assign vendor company"
      >
        <option value="">Unassigned</option>
        {(companiesQuery.data?.vendorCompanies ?? []).map((c) => (
          <option key={c.id} value={c.id}>
            {c.name}
          </option>
        ))}
      </select>
      {vendor ? (
        <p className="text-xs text-fg-muted">Technicians are emailed when assignment changes.</p>
      ) : null}
    </div>
  );
}

function VendorAckButton({
  workOrderId,
  signoffId,
  onAcknowledged,
}: {
  workOrderId: string;
  signoffId: string;
  onAcknowledged: () => void;
}) {
  const m = useMutation({
    mutationFn: () => acknowledgeVendorSignoff(workOrderId, signoffId),
    onSuccess: () => {
      toast.success('Sign-off acknowledged');
      onAcknowledged();
    },
    onError: (err) => toast.error(err instanceof ApiError ? err.message : 'Could not acknowledge'),
  });
  return (
    <Button size="sm" variant="ghost" onClick={() => m.mutate()} disabled={m.isPending}>
      Acknowledge
    </Button>
  );
}

function DL({ children }: { children: React.ReactNode }) {
  return <dl className="grid grid-cols-[100px_1fr] gap-x-4 gap-y-2">{children}</dl>;
}
function DT({ children }: { children: React.ReactNode }) {
  return <dt className="text-xs font-medium text-fg-muted">{children}</dt>;
}
function DD({ children, className }: { children: React.ReactNode; className?: string }) {
  return <dd className={cn('text-sm', className)}>{children}</dd>;
}

function DueDateInput({
  value,
  overdue,
  onChange,
  disabled,
}: {
  value: string | null;
  overdue: boolean;
  onChange: (iso: string | null) => void;
  disabled?: boolean;
}) {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(value ? value.slice(0, 10) : '');

  useEffect(() => {
    setVal(value ? value.slice(0, 10) : '');
  }, [value]);

  if (!editing) {
    return (
      <button
        type="button"
        onClick={() => setEditing(true)}
        className={cn(
          'rounded px-1 text-left hover:bg-surface-muted',
          overdue && 'font-medium text-danger',
        )}
      >
        {value ? new Date(value).toLocaleDateString() : <span className="text-fg-muted">Set due date</span>}
      </button>
    );
  }

  return (
    <div className="flex items-center gap-1">
      <Input
        type="date"
        value={val}
        onChange={(e) => setVal(e.target.value)}
        className="h-8 px-2 text-sm"
        disabled={disabled}
      />
      <Button
        size="sm"
        variant="ghost"
        onClick={() => {
          onChange(val ? new Date(val).toISOString() : null);
          setEditing(false);
        }}
      >
        <Check className="h-3.5 w-3.5" />
      </Button>
      <Button
        size="sm"
        variant="ghost"
        onClick={() => {
          setVal(value ? value.slice(0, 10) : '');
          setEditing(false);
        }}
      >
        <X className="h-3.5 w-3.5" />
      </Button>
    </div>
  );
}

function CommentComposer({ workOrderId }: { workOrderId: string }) {
  const queryClient = useQueryClient();
  const id = useId();
  const [body, setBody] = useState('');
  const mutation = useMutation({
    mutationFn: (text: string) => commentOnWorkOrder(workOrderId, { body: text }),
    onSuccess: () => {
      setBody('');
      void queryClient.invalidateQueries({ queryKey: ['work-order', workOrderId] });
    },
    onError: (err) => {
      toast.error(err instanceof ApiError ? err.message : 'Could not add comment');
    },
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Add a comment</CardTitle>
      </CardHeader>
      <CardContent>
        <form
          onSubmit={(e: FormEvent) => {
            e.preventDefault();
            const trimmed = body.trim();
            if (!trimmed) return;
            mutation.mutate(trimmed);
          }}
          className="flex flex-col gap-2"
        >
          <Label htmlFor={id} className="sr-only">
            Comment
          </Label>
          <textarea
            id={id}
            rows={3}
            maxLength={8000}
            value={body}
            onChange={(e) => setBody(e.target.value)}
            placeholder="Note for the team or next responder…"
            className="rounded-md border border-border bg-surface px-3 py-2 text-sm"
          />
          <div className="flex justify-end">
            <Button
              type="submit"
              disabled={!body.trim() || mutation.isPending}
              size="sm"
            >
              <MessageSquare className="h-3.5 w-3.5" />
              Comment
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
}

function PhotosStrip({
  workOrderId,
  attachments,
  onChange,
}: {
  workOrderId: string;
  attachments: WorkOrderAttachmentRow[];
  onChange: () => void;
}) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);

  const photos = attachments.filter((a) => a.kind === 'photo');

  async function handleFiles(files: FileList | null) {
    if (!files || files.length === 0) return;
    setUploading(true);
    try {
      for (const file of Array.from(files)) {
        if (!file.type.startsWith('image/')) continue;
        const compressed = await imageCompression(file, {
          maxSizeMB: 2,
          maxWidthOrHeight: 2048,
          useWebWorker: true,
        });
        const presigned = await presignWorkOrderAttachment(workOrderId, {
          kind: 'photo',
          filename: file.name,
          contentType: compressed.type || file.type,
          sizeBytes: compressed.size,
          caption: null,
        });
        await fetch(presigned.uploadUrl, {
          method: 'PUT',
          headers: presigned.headers,
          body: compressed,
        });
        await confirmWorkOrderAttachment(workOrderId, presigned.attachment.id, {});
      }
      onChange();
      toast.success('Photos added');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Upload failed');
    } finally {
      setUploading(false);
    }
  }

  async function handleDelete(attachmentId: string) {
    try {
      await deleteWorkOrderAttachment(workOrderId, attachmentId);
      onChange();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Delete failed');
    }
  }

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between gap-2">
        <CardTitle className="text-base">Photos</CardTitle>
        <Button
          size="sm"
          variant="secondary"
          onClick={() => fileInputRef.current?.click()}
          disabled={uploading}
        >
          <Camera className="h-3.5 w-3.5" />
          {uploading ? 'Uploading…' : 'Add photo'}
        </Button>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          multiple
          capture="environment"
          className="sr-only"
          onChange={(e) => {
            void handleFiles(e.target.files);
            e.target.value = '';
          }}
        />
      </CardHeader>
      <CardContent>
        {photos.length === 0 ? (
          <div className="flex items-center gap-2 rounded border border-dashed border-border bg-surface px-3 py-6 text-sm text-fg-secondary">
            <ImageIcon className="h-4 w-4" />
            No photos yet. Add at least one before completion.
          </div>
        ) : (
          <div className="grid grid-cols-3 gap-2 sm:grid-cols-4 md:grid-cols-5">
            {photos.map((p) => (
              <div key={p.id} className="group relative aspect-square overflow-hidden rounded-md border border-border">
                {p.downloadUrl ? (
                  <a href={p.downloadUrl} target="_blank" rel="noopener noreferrer">
                    <img
                      src={p.downloadUrl}
                      alt={p.caption ?? p.originalFilename}
                      className="h-full w-full object-cover"
                    />
                  </a>
                ) : (
                  <div className="flex h-full w-full items-center justify-center bg-surface-muted">
                    <ImageIcon className="h-6 w-6 text-fg-muted" />
                  </div>
                )}
                <button
                  type="button"
                  onClick={() => void handleDelete(p.id)}
                  className="absolute right-1 top-1 rounded-full bg-black/60 p-1 text-white opacity-0 transition-opacity group-hover:opacity-100"
                  aria-label="Delete photo"
                >
                  <Trash2 className="h-3 w-3" />
                </button>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function VoiceMemosStrip({ attachments }: { attachments: WorkOrderAttachmentRow[] }) {
  const memos = attachments.filter((a) => a.kind === 'voice_memo');
  if (memos.length === 0) return null;
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Voice memos</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {memos.map((m) => (
          <div key={m.id} className="space-y-2 rounded border border-border bg-surface px-3 py-2">
            {m.downloadUrl ? (
              // The transcript below stands in for captions; we render a
              // <track> referencing the WebVTT-shaped transcript would be
              // overkill for an internal audio attachment, so we disable
              // the caption rule for this single element.
              // eslint-disable-next-line jsx-a11y/media-has-caption
              <audio controls src={m.downloadUrl} className="w-full" preload="metadata" />
            ) : null}
            {m.transcript ? (
              <div>
                <div className="text-xs font-medium text-fg-muted">Transcript</div>
                <p className="whitespace-pre-wrap text-sm italic text-fg-secondary">
                  {m.transcript}
                </p>
              </div>
            ) : (
              <p className="text-xs text-fg-muted">
                Transcribing in the background — refresh in a moment.
              </p>
            )}
          </div>
        ))}
      </CardContent>
    </Card>
  );
}

function EmailUpdatesCard({ events }: { events: WorkOrderEventRow[] }) {
  const emailEvents = useMemo(
    () =>
      events.filter((e) => {
        const source = e.metadata?.source;
        return source === 'inbound_email' || source === 'vendor_email';
      }),
    [events],
  );

  if (emailEvents.length === 0) return null;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Email updates</CardTitle>
      </CardHeader>
      <CardContent>
        <ul className="space-y-3">
          {emailEvents.map((e) => (
            <li key={e.id} className="rounded border border-border px-3 py-2 text-sm">
              <div className="text-xs text-fg-muted">{formatDateTime(e.createdAt)}</div>
              <p className="mt-1 whitespace-pre-wrap text-fg-secondary">
                {e.body ?? 'Vendor email update'}
              </p>
            </li>
          ))}
        </ul>
        <p className="mt-3 text-xs text-fg-muted">
          Unmatched vendor emails appear in{' '}
          <Link to="/ai/review-queue" className="text-primary hover:underline">
            AI inbox
          </Link>
          .
        </p>
      </CardContent>
    </Card>
  );
}

function Timeline({
  events,
  users,
  attachments,
}: {
  events: WorkOrderEventRow[];
  users: Record<string, { id: string; name: string; email: string }>;
  attachments: WorkOrderAttachmentRow[];
}) {
  const attachmentByEventId = useMemo(() => {
    const map = new Map<string, WorkOrderAttachmentRow>();
    for (const a of attachments) {
      if (a.eventId) map.set(a.eventId, a);
    }
    return map;
  }, [attachments]);

  if (events.length === 0) {
    return <p className="text-sm text-fg-muted">No activity yet.</p>;
  }

  return (
    <ol className="space-y-3">
      {events.map((e) => {
        const author = e.actorUserId ? users[e.actorUserId] : null;
        const photo = attachmentByEventId.get(e.id);
        return (
          <li key={e.id} className="rounded-md border border-border bg-surface px-3 py-2">
            <div className="flex items-baseline justify-between text-xs">
              <span className="font-medium">
                {e.actorKind === 'system' ? 'System' : author?.name ?? 'Unknown'}
                <span className="ml-2 font-normal text-fg-muted capitalize">
                  {e.kind.replace(/_/g, ' ')}
                </span>
              </span>
              <time className="text-fg-muted" title={formatDateTime(e.createdAt)}>
                {relativeTime(e.createdAt)}
              </time>
            </div>
            {e.body ? (
              <p className="mt-1 whitespace-pre-wrap text-sm text-fg-secondary">{e.body}</p>
            ) : null}
            {photo && photo.downloadUrl ? (
              <a
                href={photo.downloadUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-2 inline-block overflow-hidden rounded border border-border"
              >
                <img
                  src={photo.downloadUrl}
                  alt={photo.caption ?? photo.originalFilename}
                  className="h-32 w-32 object-cover"
                />
              </a>
            ) : null}
            {Object.keys(e.metadata ?? {}).length > 0 ? (
              <details className="mt-1 text-xs text-fg-muted">
                <summary className="cursor-pointer select-none">
                  <ChevronDown className="inline h-3 w-3" />
                  Metadata
                </summary>
                <pre className="mt-1 overflow-x-auto rounded bg-surface-muted p-2 text-[10px]">
                  {JSON.stringify(e.metadata, null, 2)}
                </pre>
              </details>
            ) : null}
          </li>
        );
      })}
    </ol>
  );
}
